import type { ContactOutcome, ContactSubmission } from "./types";

/**
 * The module's own transport. It deliberately does not reuse `lib/access.ts`.
 *
 * "Standalone" is not tidiness here: the waitlist client and this one have
 * opposite contracts. `joinWaitlist` returns void and swallows every failure,
 * because any distinction between outcomes there is an oracle for checking
 * whether a given address is on the list. This one reports what happened,
 * because there is no such secret in a contact form and a person reporting a
 * problem must not be told "sent" when nothing was stored. Sharing a helper
 * between the two is how one of those contracts quietly becomes the other.
 *
 * The endpoint is the Cloudflare Worker, never Supabase: the `contact` schema
 * is not exposed to PostgREST and holds no grants for `anon`, so there is no
 * route from a browser to those tables at all.
 */
export async function submitContact(input: ContactSubmission): Promise<ContactOutcome> {
  const base = import.meta.env.VITE_CONTACT_WORKER_URL;
  if (!base) {
    // A build with no endpoint configured must fail visibly here rather than
    // show a success screen to somebody whose message went nowhere.
    console.error("VITE_CONTACT_WORKER_URL is not set");
    return { kind: "unavailable" };
  }

  let res: Response;
  try {
    res = await fetch(`${base}/contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: input.email.trim().toLowerCase(),
        name: input.name.trim(),
        topic: input.topic,
        message: input.message.trim(),
        token: input.token,
        source: input.source ?? null,
        campaign: input.campaign ?? null,
        // Hidden from people, irresistible to bots. Always sent empty from
        // here; the Worker answers a filled one exactly like a success, so
        // nothing is learned by filling it.
        company: "",
      }),
    });
  } catch {
    return { kind: "unavailable" };
  }

  const body = (await res.json().catch(() => null)) as
    | { ok?: boolean; ref?: string | null; error?: string }
    | null;

  if (res.ok && body?.ok) return { kind: "sent", ref: body.ref ?? null };

  switch (body?.error) {
    case "email":
      return { kind: "invalid", field: "email" };
    case "topic":
      return { kind: "invalid", field: "topic" };
    case "name":
      return { kind: "invalid", field: "name" };
    case "message_short":
    case "message_long":
    case "too_long":
      return { kind: "invalid", field: "message" };
    case "throttled":
      return { kind: "throttled" };
    case "challenge":
      return { kind: "challenge" };
    case "closed":
      return { kind: "closed" };
    default:
      return { kind: "unavailable" };
  }
}
